import { notUndefined } from '@go-shared/lib/functional';
import React, { Fragment } from 'react';
import { DefaultedText } from '../DefaultedText';
import BubbleButton, { BubbleButtonProps } from './BubbleButton';
import BubbleCheckbox, { BubbleCheckboxProps } from './BubbleCheckbox';

interface BubbleCheckboxGroupPropsBase<T> {
  options: T[];
  vertical?: boolean;
  allowEmpty?: boolean;
  readOnly?: boolean;
  labelExtractor?: (option: T) => string;
  containerClassName?: string;
}

interface BubbleCheckboxGroupAsButtonPropsBase<T> {
  optionAs: 'button';
  variant: BubbleButtonProps['variant'] | ((option: T) => BubbleButtonProps['variant']);
}

interface BubbleCheckboxGroupAsCheckboxPropsBase {
  optionAs?: 'checkbox';
}

type OmittedButtonProps = Omit<BubbleButtonProps, 'onClick' | 'variant'>;
type OmittedInputProps<T> = Omit<
  BubbleCheckboxProps<T>,
  'type' | 'label' | 'checked' | 'onValue' | 'value'
>;

type BubbleCheckboxGroupAsButton<T> = BubbleCheckboxGroupAsButtonPropsBase<T> & OmittedButtonProps;
type BubbleCheckboxGroupAsCheckbox<T> = BubbleCheckboxGroupAsCheckboxPropsBase &
  OmittedInputProps<T>;

interface BubbleCheckboxGroupSingleModeProps<T> {
  value: T;
  isSingleMode: true;
  onValue?: (value: T) => void;
}

interface BubbleCheckboxGroupMultiModeProps<T> {
  value: T[];
  isSingleMode?: false;
  onValue?: (value: T[]) => void;
}

export type BubbleCheckboxGroupProps<T> = BubbleCheckboxGroupPropsBase<T> &
  (BubbleCheckboxGroupAsButton<T> | BubbleCheckboxGroupAsCheckbox<T>) &
  (BubbleCheckboxGroupSingleModeProps<T> | BubbleCheckboxGroupMultiModeProps<T>);

export default function BubbleCheckboxGroup<T>(props: BubbleCheckboxGroupProps<T>) {
  const {
    value: _value,
    onValue: _onValue,
    options,
    vertical,
    isSingleMode,
    allowEmpty,
    readOnly,
    labelExtractor: propLabelExtractor,
    className: propClassName,
    containerClassName: propContainerClassName,
    variant,
    optionAs,
    ...rest
  } = props;

  const internalValue = isSingleMode ? [props.value] : props.value;

  function handleCheckboxCheck(checked: boolean, option: T) {
    const newValue = isSingleMode
      ? checked
        ? [option]
        : []
      : checked
        ? [...internalValue, option]
        : internalValue.filter(v => v !== option);

    const newOptions = options.filter(o => newValue.includes(o));

    if (!allowEmpty && newOptions.length === 0) {
      if (options.length < 1) {
        throw new Error('Cannot have empty options array if allowEmpty is false');
      }
      newOptions.push(options[0]);
    }

    if (isSingleMode) {
      props.onValue?.(newOptions[0]);
    } else {
      props.onValue?.(newOptions);
    }
  }

  const classes: string[] = [`flex gap-2 ${vertical ? 'flex-col items-start' : 'items-center'}`];
  if (propClassName) classes.push(propClassName);

  const labelExtractor = propLabelExtractor || ((opt: T) => `${opt}`);

  function elemToUse(opt: T) {
    const isSelected = internalValue.includes(opt);
    let variantToUse: BubbleButtonProps['variant'] = 'navy';

    if (optionAs === 'button') {
      variantToUse = typeof variant === 'function' ? variant(opt) : variant;
    }

    const finalClasses = [`border border-${variantToUse}`, ...classes];

    return optionAs === 'button' ? (
      <BubbleButton
        {...(rest as OmittedButtonProps)}
        className={finalClasses.join(' ')}
        variant={isSelected ? variantToUse : 'white'}
        onClick={() => handleCheckboxCheck(!internalValue.includes(opt), opt)}
      >
        <span className={`${isSelected ? 'text-white' : `text-${variantToUse}`}`}>
          {labelExtractor(opt)}
        </span>
      </BubbleButton>
    ) : (
      <BubbleCheckbox
        {...(rest as OmittedInputProps<T>)}
        className={classes.join(' ')}
        checked={internalValue.includes(opt)}
        label={labelExtractor(opt)}
        onValue={chk => handleCheckboxCheck(chk, opt)}
      />
    );
  }

  const readOnlyVals = internalValue.filter(notUndefined).map(labelExtractor);

  const containerClasses = [`flex gap-2 ${vertical ? 'flex-col items-start' : 'items-center'}`];
  if (propContainerClassName) containerClasses.push(propContainerClassName);

  return (
    <div className={containerClasses.join(' ')}>
      {readOnly ? (
        <DefaultedText val={readOnlyVals.join(', ')} />
      ) : (
        props.options.map((opt, i) => <Fragment key={i}>{elemToUse(opt)}</Fragment>)
      )}
    </div>
  );
}
